import type { AccountType } from '@pfo/domain';

import { cx } from '../../../shared/lib/cx.js';
import { AccountTypeIcon } from '../../../shared/ui/icons/AccountTypeIcon.js';
import styles from './QuickAddControls.module.css';

export type ChipItem = Readonly<{
  id: string;
  label: string;
  /** Draws the matching account icon; omitted for things that are not accounts. */
  accountType?: AccountType;
}>;

/**
 * A horizontal row of choices — accounts, income sources — that scrolls instead of wrapping.
 *
 * Chips rather than a `<select>`: one tap, no keyboard, no precision needed, and the current
 * choice is visible without opening anything (skill 12.2). A native select is still the right
 * control when the list is long, which is why the category grid has its own "all" panel.
 */
export function ChipRow({
  label,
  items,
  selectedId,
  onSelect,
  emptyMessage,
}: {
  label: string;
  items: readonly ChipItem[];
  selectedId: string;
  onSelect: (id: string) => void;
  emptyMessage?: string;
}) {
  return (
    <section className={styles.section}>
      <p className={styles.sectionLabel}>{label}</p>

      {items.length === 0 ? (
        <p className={styles.hint}>{emptyMessage ?? 'Chưa có lựa chọn nào.'}</p>
      ) : (
        <div className={styles.chipRow} role="group" aria-label={label}>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              className={cx(styles.chip, selectedId === item.id && styles.chipActive)}
              aria-pressed={selectedId === item.id}
              onClick={() => onSelect(item.id)}
            >
              {item.accountType ? <AccountTypeIcon accountType={item.accountType} /> : null}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
